import * as THREE from 'three'
import gsap from 'gsap'
import Producer from '../Producer.js'
import Square from './Square.js'
import { DURATION, EASE, STAGGER } from '../../shared/motion.js'
import { disposeMaterial } from '../utils/dispose.js'

// ─────────────────────────────────────────────────────────────
// Hero 落地座標計算
// ─────────────────────────────────────────────────────────────

/**
 * Detail 頁的圖片列表是純 HTML/CSS（見 Detail.js 的 .detail__gallery），這裡只負責一件事：
 * 告訴 leaveToDetail() 的 hero mesh 該飛去哪。落地座標直接量測 DOM
 * （Detail.js 的 .detail__gallery-target，一個置中於 .detail__gallery 欄位內、固定 16/10 的靜態標記元素），
 * 不是靠一組獨立維護的常數去逼近 CSS 版面。
 */
const DETAIL = Object.freeze({
    /**
     * @param {Viewport} viewport
     * @param {HTMLElement} targetEl Detail.js 的 .detail__gallery-target
     * @returns {{ width: number, height: number, pos: number[] }}
     */
    computeFromDOM(viewport, targetEl) {
        const rect = targetEl.getBoundingClientRect()

        const width = viewport.pxToWorld(rect.width, 0)
        const height = viewport.pxToWorld(rect.height, 0)
        const p = viewport.screenToWorld(rect.left + rect.width / 2, rect.top + rect.height / 2, 0)

        return {
            width,
            height,
            pos: [p.x, p.y, 0]
        }
    }
})

// ─────────────────────────────────────────────────────────────
// Mode Configurations & Constants
// ─────────────────────────────────────────────────────────────

/**
 * 轉盤模式設定 (Straight ↔ Curve)，pc/mobile 兩層各自一組
 * R: 弧半徑 (1/R 為曲率，越大越平)
 */
const MODE_CONFIG = Object.freeze({
    pc: {
        straight: {
            pos: [1.504, -0.2035, 0.8766],
            rot: [-0.471, -0.9943, -0.4167],
            R: 10000,
            SPACING: 1.45,
            VISIBLE: 14,
            SENS: 0.001,
            DAMP: 0.1,
            DEPTH: 0,
            FADE: 3,
            SIZE: 1
        },
        curve: {
            pos: [-2.139, -0.087, -1.8384],
            rot: [0.4211, -0.9008, 0.2304],
            R: 7.5188,
            SPACING: 1.45,
            VISIBLE: 15,
            SENS: 0.0012,
            DAMP: 0.08,
            DEPTH: 0,
            FADE: 3,
            SIZE: 1
        }
    },
    // TODO: 先求有框架，數值是 pc 版的複製佔位；之後開 #debug、縮到手機尺寸用 gizmo 調整後貼回這裡
    mobile: {
        straight: {
            pos: [0.4051, 0.1742, -1.3432],
            rot: [-0.3539, -0.9467, -0.6677],
            R: 10000,
            SPACING: 1.45,
            VISIBLE: 15,
            SENS: 0.001,
            DAMP: 0.1,
            DEPTH: 0,
            FADE: 3,
            SIZE: 1
        },
        curve: {
            pos: [0.0247, -0.2158, -1.8384],
            rot: [0.2288, -0.6012, 0.1187],
            R: 7.5188,
            SPACING: 1.45,
            VISIBLE: 15,
            SENS: 0.0012,
            DAMP: 0.08,
            DEPTH: 0,
            FADE: 3,
            SIZE: 1
        }
    }
})

const SHAPE_KEYS = Object.freeze(['SPACING', 'VISIBLE', 'SENS', 'DAMP', 'DEPTH', 'FADE', 'SIZE'])

// 響應式插值的寬度區間 (px)：>= PC_WIDTH 完全套用 pc 版，<= MOBILE_WIDTH 完全套用 mobile 版，中間線性混合。
// 跟 variables.css 的斷點數字一樣，只是先佔位；這裡是縮放感覺的美術決定，不強制要跟斷點分類共用同一組數字。
const PC_WIDTH = 1024
const MOBILE_WIDTH = 375

// ─────────────────────────────────────────────────────────────
// Carousel System
// ─────────────────────────────────────────────────────────────

export default class Carousel {
    constructor(squares) {
        this.producer = new Producer()
        this.scene = this.producer.scene
        this.interaction = this.producer.interaction
        this.camera = this.producer.camera.instance
        this.debug = this.producer.debug

        // 每幀重用之暫存純量/矩陣（防 GC 產生）
        this._tmpWorld = new THREE.Vector3()
        this._yAxis = new THREE.Vector3(0, 1, 0)

        // 模式切換矩陣與 Euler 暫存
        this._groupFromPos = new THREE.Vector3()
        this._groupToPos = new THREE.Vector3()
        this._groupFromQuat = new THREE.Quaternion()
        this._groupToQuat = new THREE.Quaternion()
        this._euler = new THREE.Euler()

        // 狀態控管
        this.mode = 'curve'
        this.modeT = 1
        this._shapeFrom = {}
        this._shapeTo = null

        // 卡片實例來源
        this.squares = squares
        this.count = squares.length

        // 播報與高階 Callback 註冊
        this.onHover = null
        this.onCenterChange = null
        this.onSelect = null
        this._centerProject = null
        this._pendingHero = null
        this._hero = null
        this._leaveCall = null

        // 初始化外觀參數 (Tunables)
        this._initTunables()

        // 互動與滾動狀態：_position 是畫面用的阻尼後數值，_target 是輸入累加的目標值
        this._position = 0
        this._target = 0
        this.enabled = false
        this.fadeStrength = 0

        // 建立幾何、事件與 debug 介面
        this._buildPool()
        this._setupGroup()
        this._setupInput()
        this.producer.debug.addCarousel(this)
    }

    // ─────────────────────────────────────────────────────────────
    // Setup & Initialization Helpers
    // ─────────────────────────────────────────────────────────────

    /** 依預設模式載入基本參數 */
    _initTunables() {
        this._deviceT = this._computeDeviceT()

        const cfg = this._blendConfig(this.mode, this._deviceT)
        this.R = cfg.R
        this.SPACING = cfg.SPACING
        this.VISIBLE = cfg.VISIBLE
        this.SENS = cfg.SENS
        this.DAMP = cfg.DAMP
        this.DEPTH = cfg.DEPTH
        this.FADE = cfg.FADE
        this.SIZE = cfg.SIZE
    }

    /** 依目前視窗寬度換算 pc/mobile 混合係數 (0 = 完全 pc，1 = 完全 mobile) */
    _computeDeviceT() {
        const width = this.producer.sizes.width
        const t = (PC_WIDTH - width) / (PC_WIDTH - MOBILE_WIDTH)
        return THREE.MathUtils.clamp(t, 0, 1)
    }

    /** 混合同一個 mode (straight/curve) 底下的 pc/mobile 設定，回傳完整設定物件 */
    _blendConfig(mode, t) {
        const a = MODE_CONFIG.pc[mode]
        const b = MODE_CONFIG.mobile[mode]

        const blended = {
            pos: a.pos.map((v, i) => {
                return THREE.MathUtils.lerp(v, b.pos[i], t)
            }),
            rot: a.rot.map((v, i) => {
                return THREE.MathUtils.lerp(v, b.rot[i], t)
            }),
            R: 1 / THREE.MathUtils.lerp(1 / a.R, 1 / b.R, t)
        }
        for (const key of SHAPE_KEYS) {
            blended[key] = THREE.MathUtils.lerp(a[key], b[key], t)
        }
        return blended
    }

    /** 建立無縫循環物件池 (Object Pool) */
    _buildPool() {
        const total = this.count
        const maxMode = Math.ceil(
            Math.max(
                ...Object.values(MODE_CONFIG).flatMap((tier) => {
                    return Object.values(tier).map((c) => {
                        return c.VISIBLE
                    })
                })
            )
        )
        this._maxVisible = Math.max(total, maxMode, 15)

        // 為確保接縫連續不重複，以倍數複製卡片
        const copies = Math.max(1, Math.ceil((this._maxVisible + 1) / total))
        this.poolLen = total * copies

        this.units = this.squares.slice()
        for (let j = total; j < this.poolLen; j++) {
            const src = this.squares[j % total]
            const clone = new Square(0, 0, 0, src.hw, src.hh, src.project, j)

            // 複製 Shader Uniform 參照與貼圖
            clone.material.uniforms.uTexture.value = src.material.uniforms.uTexture.value
            clone.material.uniforms.uTextureAspect.value = src.material.uniforms.uTextureAspect.value
            clone.material.uniforms.uHasImage.value = src.material.uniforms.uHasImage.value

            clone._src = src // 動畫鏡射來源
            this.units.push(clone)
        }
    }

    /** 設定 3D 容器 (Group) 並掛載 Mesh */
    _setupGroup() {
        this.group = new THREE.Group()

        const cfg = this._blendConfig(this.mode, this._deviceT)
        this.group.position.set(...cfg.pos)
        this.group.rotation.set(...cfg.rot)

        this.scene.add(this.group)
        for (const unit of this.units) {
            this.group.add(unit.mesh)
        }
    }

    /** 初始化並綁定互動監聽 */
    _setupInput() {
        this.interaction.onScroll = (deltaX, deltaY) => {
            if (!this.enabled) {
                return
            }
            // 水平轉盤：取兩軸中位移較大者作為捲動量 (支援 Mac 觸控板雙指左右滑動，以及觸控划動)
            const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY
            this._target += delta * this.SENS
        }

        this.interaction.onClick = (owner) => {
            if (this.debug.active) {
                return
            }
            if (!this.enabled) {
                return
            }
            this._pendingHero = owner
            this.onSelect?.(owner.project)
        }

        this.interaction.onHoverEnter = (owner) => {
            if (!this.enabled) {
                return
            }
            this._leaveCall?.kill()
            this._leaveCall = null

            this.producer.canvas.style.cursor = owner.project.enable ? 'pointer' : 'not-allowed'
            this._applyHover(owner)
            this.onHover?.(owner.project)
        }

        this.interaction.onHoverLeave = () => {
            if (!this.enabled) {
                return
            }
            this._leaveCall?.kill()

            // 延遲套用離場，避免快速滑過卡片時產生中間空檔與閃爍
            this._leaveCall = gsap.delayedCall(0.06, () => {
                this.producer.canvas.style.cursor = ''
                this._applyHover(null)
                this.onHover?.(null)
            })
        }
    }

    // ─────────────────────────────────────────────────────────────
    // Interaction Controls & Raycasting
    // ─────────────────────────────────────────────────────────────

    enable() {
        this.enabled = true
    }

    disable() {
        this.enabled = false
        this._leaveCall?.kill()
        this._leaveCall = null

        this.producer.canvas.style.cursor = ''
        this._applyHover(null)
        this.onHover?.(null)
    }

    /** 處理 Hover 狀態的深淺色變更 */
    _applyHover(owner) {
        for (const unit of this.units) {
            gsap.to(unit.material.uniforms.uDim, {
                value: owner ? (unit === owner ? 1 : 0) : 1,
                duration: DURATION.color,
                ease: EASE.hover,
                overwrite: true
            })
        }
    }

    /** 管理單張卡片是否可被滑鼠 Raycast 命中 */
    _setInteractive(sq, on) {
        if (sq._interactive === on) {
            return
        }
        sq._interactive = on

        if (on) {
            this.interaction.register(sq.mesh, sq)
        } else {
            this.interaction.unregister(sq.mesh)
        }
    }

    /** 當滾動使中心卡片改變時廣播 */
    _updateCenter() {
        const idx = gsap.utils.wrap(0, this.poolLen, Math.round(this._position))
        const project = this.units[idx].project

        if (project !== this._centerProject) {
            this._centerProject = project
            this.onCenterChange?.(project)
        }
    }

    // ─────────────────────────────────────────────────────────────
    // Mode Transitions (Straight ↔ Curve)
    // ─────────────────────────────────────────────────────────────

    /** 由 Director 的 resize pipeline 驅動：依當前寬度重新混合 pc/mobile 設定 */
    resize() {
        this._deviceT = this._computeDeviceT()

        // straight/curve 的 GSAP tween 正在跑的話讓它自己收斂，避免兩邊同時搶著寫入
        if (this.modeT < 1) {
            return
        }

        const cfg = this._blendConfig(this.mode, this._deviceT)
        this.R = cfg.R
        for (const key of SHAPE_KEYS) {
            this[key] = cfg[key]
        }
        this.group.position.set(...cfg.pos)
        this.group.rotation.set(...cfg.rot)
    }

    setMode(mode) {
        if (mode === this.mode) {
            return
        }
        this.mode = mode

        const cfg = this._blendConfig(mode, this._deviceT)
        this._groupFromPos.copy(this.group.position)
        this._groupFromQuat.copy(this.group.quaternion)
        this._groupToPos.set(...cfg.pos)
        this._groupToQuat.setFromEuler(this._euler.set(...cfg.rot))

        this._shapeFrom.R = this.R
        for (const key of SHAPE_KEYS) {
            this._shapeFrom[key] = this[key]
        }
        this._shapeTo = cfg

        this.modeT = 0
        gsap.to(this, {
            modeT: 1,
            duration: DURATION.move,
            ease: EASE.move,
            overwrite: true,
            onUpdate: () => {
                const t = this.modeT
                this.group.position.lerpVectors(this._groupFromPos, this._groupToPos, t)
                this.group.quaternion.slerpQuaternions(this._groupFromQuat, this._groupToQuat, t)
                this._lerpShape(t)
            }
        })
    }

    /** 立即（無動畫）套回當前 mode 的 MODE_CONFIG 設定值，供 debug 面板 reset 用 */
    resetToModeConfig() {
        const cfg = this._blendConfig(this.mode, this._deviceT)

        this.R = cfg.R
        for (const key of SHAPE_KEYS) {
            this[key] = cfg[key]
        }

        this.group.position.set(...cfg.pos)
        this.group.rotation.set(...cfg.rot)
    }

    resetOpacity() {
        this.squares.forEach((sq) => {
            gsap.killTweensOf(sq.material.uniforms.uOpacity)
            sq.material.uniforms.uOpacity.value = 0
        })
    }

    /** 補間外觀參數 (1/R 曲率特別插值以維持過渡平滑) */
    _lerpShape(t) {
        const a = this._shapeFrom
        const b = this._shapeTo

        this.R = 1 / THREE.MathUtils.lerp(1 / a.R, 1 / b.R, t)
        for (const key of SHAPE_KEYS) {
            this[key] = THREE.MathUtils.lerp(a[key], b[key], t)
        }
    }

    get curvature() {
        return 1 / this.R
    }

    set curvature(k) {
        // 保留正負號：k>0 沿一般方向彎（凹向鏡頭），k<0 反向彎（凸向鏡頭），
        // 只夾住絕對值避免 k 趨近 0 時 R 發散到無限大
        const sign = k < 0 ? -1 : 1
        this.R = sign / Math.max(Math.abs(k), 1e-4)
    }

    // ─────────────────────────────────────────────────────────────
    // Detail View Transitions (Hero Card Animation)
    // ─────────────────────────────────────────────────────────────

    /** 卡片飛出並放至 Detail 頁面的 Hero 位 */
    leaveToDetail(project) {
        this.disable()

        const clicked = this._pendingHero
        this._pendingHero = null
        const hero = clicked && clicked.project === project ? clicked : this._pickHero(project)
        this._hero = hero

        const detail = DETAIL.computeFromDOM(this.producer.viewport, this.producer.detail.getGalleryTargetEl())

        this.group.updateMatrixWorld()
        const localPos = this.group.worldToLocal(new THREE.Vector3(...detail.pos))
        const localQuat = this.group.quaternion.clone().invert()
        const fromQuat = hero.mesh.quaternion.clone()
        const q = { t: 0 }

        const tl = gsap.timeline()

        // 1. 非目標卡片淡出
        for (const unit of this.units) {
            if (unit === hero) {
                continue
            }
            tl.to(
                unit.material.uniforms.uOpacity,
                {
                    value: 0,
                    duration: DURATION.spread,
                    ease: EASE.out
                },
                0
            )
        }

        // 2. Target Hero 卡片平移至 Detail 核心區域
        tl.to(
            hero.mesh.position,
            {
                x: localPos.x,
                y: localPos.y,
                z: localPos.z,
                duration: DURATION.move,
                ease: EASE.move
            },
            0
        )

        // 3. 旋轉正對鏡頭
        tl.to(
            q,
            {
                t: 1,
                duration: DURATION.move,
                ease: EASE.move,
                onUpdate: () => {
                    hero.mesh.quaternion.slerpQuaternions(fromQuat, localQuat, q.t)
                }
            },
            0
        )

        // 4. 縮放與 Shader 切割尺寸調整
        tl.to(
            hero.mesh.scale,
            {
                x: detail.width,
                y: detail.height,
                z: 1,
                duration: DURATION.move,
                ease: EASE.move
            },
            0
        )
        tl.to(
            hero.material.uniforms.uScale.value,
            {
                x: detail.width,
                y: detail.height,
                duration: DURATION.move,
                ease: EASE.move
            },
            0
        )

        // 5. 解除 3D 弧形彎曲
        tl.to(
            hero.material.uniforms.uBend,
            {
                value: 0,
                duration: DURATION.move,
                ease: EASE.move
            },
            0
        )

        return tl
    }

    hideHeroInstant() {
        gsap.killTweensOf(this._hero.material.uniforms.uOpacity)
        this._hero.material.uniforms.uOpacity.value = 0
    }

    /** 搜尋離中央最近的指定專案卡片作為 Hero 卡 */
    _pickHero(project) {
        const ring = this.poolLen
        let best = null
        let bestOff = Infinity

        for (let j = 0; j < ring; j++) {
            const unit = this.units[j]
            if (unit.project !== project || !unit.mesh.visible) {
                continue
            }

            let off = j - this._position
            off -= ring * Math.round(off / ring)

            if (Math.abs(off) < bestOff) {
                bestOff = Math.abs(off)
                best = unit
            }
        }

        return (
            best ??
            this.units.find((u) => {
                return u.project === project
            })
        )
    }

    /** 進入 About 等 Overlay 時淡出所有卡片（含無圖佔位線框）並停用互動 */
    fadeOut() {
        this.disable()

        const opacities = this.squares.map((sq) => {
            return sq.material.uniforms.uOpacity
        })

        gsap.killTweensOf(opacities)

        const tween = gsap.to(opacities, {
            value: 0,
            duration: DURATION.conceal,
            ease: EASE.out
            // stagger: STAGGER.tight
        })

        const tl = gsap.timeline()
        tl.add(tween, 0)
        return tl
    }

    /** 從 Detail 或 About 返回 Home 頁時淡入所有卡片（含無圖佔位線框） */
    fadeIn() {
        const opacities = this.squares.map((sq) => {
            return sq.material.uniforms.uOpacity
        })

        gsap.killTweensOf(opacities)

        const tween = gsap.to(opacities, {
            value: 1,
            duration: DURATION.reveal,
            ease: EASE.out
            // stagger: STAGGER.tight
        })

        const tl = gsap.timeline()
        tl.add(tween, 0)
        return tl
    }

    // ─────────────────────────────────────────────────────────────
    // Core Arc Geometry Engine (Per-frame Update Loop)
    // ─────────────────────────────────────────────────────────────

    update() {
        this._position += (this._target - this._position) * this.DAMP

        const ring = this.poolLen
        const halfWin = (this.VISIBLE - 1) / 2
        const denom = Math.max(halfWin, 1e-4)
        const cutoff = halfWin + 0.5
        const fadeBand = Math.max(this.FADE, 1e-4)

        this.group.updateMatrixWorld()

        for (let j = 0; j < ring; j++) {
            const sq = this.units[j]

            let off = j - this._position
            off -= ring * Math.round(off / ring)
            const absOff = Math.abs(off)

            // 裁切視窗外卡片
            if (absOff > cutoff) {
                sq.mesh.visible = false
                this._setInteractive(sq, false)
                continue
            }
            sq.mesh.visible = true

            this._mirrorClone(sq)
            this._placeOnArc(sq, off)
            this._setRenderOrder(sq, absOff)

            // 算計算邊緣 Fade 及可互動狀態
            const edge = this._edgeFade(absOff, cutoff, fadeBand)
            sq.material.uniforms.uFade.value = 1 - this.fadeStrength * (1 - edge)
            this._setInteractive(sq, edge >= 1)

            this._applyDepth(sq, absOff, denom)
        }

        this._updateCenter()
    }

    /** 鏡射 Clone 源頭的動畫 Uniform */
    _mirrorClone(sq) {
        if (!sq._src) {
            return
        }
        const su = sq._src.material.uniforms
        const du = sq.material.uniforms

        du.uOpacity.value = su.uOpacity.value
        du.uTextureMix.value = su.uTextureMix.value
        du.uImageScale.value = su.uImageScale.value
    }

    /** 依據角度與曲率 (R) 將卡片座標與角度貼合至圓弧上 */
    _placeOnArc(sq, off) {
        const angle = (off * this.SPACING) / this.R

        sq.mesh.position.set(this.R * Math.sin(angle), 0, this.R * Math.cos(angle) - this.R)
        sq.mesh.quaternion.setFromAxisAngle(this._yAxis, angle)

        sq.material.uniforms.uBend.value = 1
        sq.material.uniforms.uBendRadius.value = this.R
    }

    /** 依相機距離計算 Render Order 防止繪製穿透與閃爍 */
    _setRenderOrder(sq, absOff) {
        this._tmpWorld.copy(sq.mesh.position).applyMatrix4(this.group.matrixWorld)
        sq.mesh.renderOrder = -(this._tmpWorld.distanceTo(this.camera.position) + absOff * 1e-4)
    }

    /** 平滑邊緣淡出 (Smoothstep) */
    _edgeFade(absOff, cutoff, fadeBand) {
        const fade = THREE.MathUtils.clamp((cutoff - absOff) / fadeBand, 0, 1)
        return fade * fade * (3 - 2 * fade)
    }

    /** 離中心越遠則進行景深縮放 */
    _applyDepth(sq, absOff, denom) {
        const t = 1 - Math.min(absOff / denom, 1)
        const f = 1 - (1 - t) * this.DEPTH
        const size = sq.hw * 2 * this.SIZE

        sq.mesh.scale.set(size * f, size * f, 1)
        sq.material.uniforms.uScale.value.set(size * f, size * f)
    }

    // ─────────────────────────────────────────────────────────────
    // Resource Lifecycle & Cleanup
    // ─────────────────────────────────────────────────────────────

    destroy() {
        this.disable()

        // 移除複製出的非原始 Square Pool 資源
        for (let j = this.count; j < this.units.length; j++) {
            const clone = this.units[j]
            this._setInteractive(clone, false)
            // 不 dispose geometry：clone 的 mesh 沿用 Square 的 SHARED_GEOMETRY 單例，並非 clone 專屬資源
            if (clone.material) {
                disposeMaterial(clone.material)
            }
        }

        if (this.group) {
            this.scene.remove(this.group)
        }
    }
}
